import React from 'react';
import clsx from 'clsx';
import { NavLink, useMatch, useNavigate, useOutlet } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';

interface PageNavigationRoute {
  path: string;
  title: string;
  count?: number;
  hidden?: boolean;
}

interface PageNavigationProps {
  routes: PageNavigationRoute[];
  /**
   * Optional actions rendered right-aligned on the same row as the tab list.
   * Mirrors the Styles-tab pattern: the nav becomes a `flex justify-between`
   * container so a "Create …" button can sit on the far right without a
   * separate header row.
   */
  actions?: React.ReactNode;
}

/**
 * The child route, cross-faded on navigation. Pages whose tab strip moved into
 * the sidebar render this on its own, so switching tabs animates the same way
 * whether the tabs are drawn here or in the sidebar.
 */
export const PageOutlet = () => {
  const outlet = useOutlet();

  return (
    <AnimatePresence mode="wait" initial>
      {outlet && React.cloneElement(outlet, {})}
    </AnimatePresence>
  );
};

export const PageNavigation = (props: React.PropsWithChildren<PageNavigationProps>) => {
  const match = useMatch('/');
  const navigate = useNavigate();
  const routes = props.routes.filter((route) => !route.hidden);

  return (
    <div className="mt-2">
      <div className="sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        <select
          id="tabs"
          name="tabs"
          value={routes.find((tab) => match?.pathname.includes(tab.path))?.path}
          onChange={(e) => navigate(e.currentTarget.value)}
          className="block w-full rounded-md border-neutral-300 dark:border-neutral-600 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
        >
          {routes.map((tab) => (
            <option key={tab.path} value={tab.path}>
              {tab.title}
            </option>
          ))}
        </select>
        {props.actions && <div className="mt-3">{props.actions}</div>}
      </div>
      <div className="hidden sm:block">
        <div className="flex items-center justify-between gap-4 border-b border-neutral-200 dark:border-neutral-700">
          <nav
            aria-label="Tabs"
            className="-mb-px flex min-w-0 flex-1 gap-x-6 overflow-x-auto lg:gap-x-8"
          >
            {routes.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  clsx(
                    isActive
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:border-neutral-200 dark:hover:border-white/10 hover:text-neutral-700 dark:hover:text-neutral-300',
                    'flex shrink-0 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {tab.title}
                    {tab.count ? (
                      <span
                        className={clsx(
                          isActive
                            ? 'bg-primary-200/65 dark:bg-primary-500/25 text-primary-600 dark:text-primary-400'
                            : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-white',
                          'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block'
                        )}
                      >
                        {tab.count}
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          {props.actions && <div className="mb-px flex-none">{props.actions}</div>}
        </div>
      </div>
      <div className="mt-8">
        <PageOutlet />
      </div>
    </div>
  );
};
